/**
 * Cart drawer "Pair it with" complementary product recommendations.
 *
 * This file is loaded with `defer` on every page (same as the rest of the
 * cart-drawer scripts), but it does nothing on load. All it does is define
 * a custom element and expose window.CartDrawerRecommendations.refresh().
 * The actual network request only happens when something calls refresh() —
 * which cart-drawer.js does from open(), i.e. only once a shopper actually
 * opens the cart drawer. That keeps this feature at effectively zero cost
 * to initial page load / Core Web Vitals, which is why the previous
 * "pair it with" app was removed.
 */
(function () {
  if (customElements.get("cart-drawer-recommendations")) return;

  const RECOMMENDATIONS_ENDPOINT = "/recommendations/products.json";
  const MAX_ITEMS = 4;
  const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  function escapeHtml(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, (char) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]
    ));
  }

  // Formats an integer cent amount using the shop's own money_format string
  // (passed in via data-money-format), the same tokens Shopify uses in
  // Liquid: {{amount}}, {{amount_no_decimals}},
  // {{amount_with_comma_separator}}, {{amount_no_decimals_with_comma_separator}}.
  function formatMoney(cents, format) {
    const numericCents = typeof cents === "number" ? cents : parseInt(cents, 10) || 0;
    const value = (numericCents / 100).toFixed(2);
    const [whole, decimals] = value.split(".");
    const withComma = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    const replacements = {
      amount: value,
      amount_no_decimals: whole,
      amount_with_comma_separator: `${withComma}.${decimals}`,
      amount_no_decimals_with_comma_separator: withComma,
    };

    const template = format && /\{\{\s*\w+\s*\}\}/.test(format) ? format : "${{amount}}";
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
      Object.prototype.hasOwnProperty.call(replacements, key) ? replacements[key] : replacements.amount,
    );
  }

  function readCache(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
      return parsed.products;
    } catch (e) {
      return null;
    }
  }

  function writeCache(key, products) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), products }));
    } catch (e) {
      // ignore quota / private-mode storage errors
    }
  }

  function cardMarkup(product, moneyFormat) {
    const image = product.featured_image || (product.images && product.images[0]);
    const imageUrl = typeof image === "string" ? image : image && image.url;
    const variant = product.variants && product.variants[0];
    const price = variant ? variant.price : product.price;
    const available = !variant || variant.available !== false;

    return (
      '<li class="cart-drawer-recommendations__item">' +
      `<a href="${escapeHtml(product.url)}" class="cart-drawer-recommendations__link">` +
      (imageUrl
        ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(product.title)}" loading="lazy" width="72" height="72">`
        : "") +
      `<span class="cart-drawer-recommendations__title">${escapeHtml(product.title)}</span>` +
      (price !== undefined && price !== null
        ? `<span class="cart-drawer-recommendations__price">${formatMoney(price, moneyFormat)}</span>`
        : "") +
      "</a>" +
      (variant && available
        ? `<button type="button" class="cart-drawer-recommendations__add" data-quick-add data-variant-id="${escapeHtml(variant.id)}" aria-label="Add ${escapeHtml(product.title)} to cart">+</button>`
        : "") +
      "</li>"
    );
  }

  class CartDrawerRecommendations extends HTMLElement {
    // This element gets destroyed and recreated whenever the theme
    // re-renders #CartDrawer's innerHTML (e.g. right after add-to-cart,
    // moments after the drawer's "active" class first appears) — so the
    // drawer-level open watcher below can fire on an instance that's about
    // to be thrown away. connectedCallback() fires every time an element
    // is (re-)inserted into the document, so use it to self-trigger
    // whenever this element lands inside an already-open drawer.
    connectedCallback() {
      const drawer = document.querySelector("cart-drawer");
      if (drawer && drawer.classList.contains("active")) {
        this.refresh();
      }
    }

    // Fetches (or reuses a cached copy of) complementary recommendations
    // for whatever is currently in the cart, then renders them. Safe to
    // call repeatedly — it no-ops if the cart contents haven't changed
    // since the last successful render.
    refresh() {
      const productIds = (this.dataset.productIds || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      if (!productIds.length) {
        this.hidden = true;
        return;
      }

      const cacheKey = `cart-drawer-recs:${productIds.slice().sort().join(",")}`;
      if (this._activeKey === cacheKey) return;

      const cached = readCache(cacheKey);
      if (cached) {
        this._activeKey = cacheKey;
        this.renderProducts(cached, productIds);
        return;
      }

      // Seed the recommendation call with the most recently added line
      // item — it's the most contextually relevant "pair it with" anchor
      // when there are several different products in the cart.
      const seedId = productIds[productIds.length - 1];
      const url =
        `${RECOMMENDATIONS_ENDPOINT}?product_id=${encodeURIComponent(seedId)}` +
        `&intent=complementary&limit=${MAX_ITEMS + productIds.length}`;

      fetch(url, { credentials: "same-origin" })
        .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
        .then((data) => {
          const products = data && Array.isArray(data.products) ? data.products : [];
          writeCache(cacheKey, products);
          this._activeKey = cacheKey;
          this.renderProducts(products, productIds);
        })
        .catch(() => {
          this.hidden = true;
        });
    }

    renderProducts(products, cartProductIds) {
      const list = this.querySelector("[data-recommendations-list]");
      if (!list) return;

      const excluded = new Set(cartProductIds.map(String));
      const filtered = (products || [])
        .filter((product) => !excluded.has(String(product.id)))
        .slice(0, MAX_ITEMS);

      if (!filtered.length) {
        this.hidden = true;
        list.innerHTML = "";
        return;
      }

      const moneyFormat = this.dataset.moneyFormat;
      list.innerHTML = filtered.map((product) => cardMarkup(product, moneyFormat)).join("");

      list.querySelectorAll("[data-quick-add]").forEach((button) => {
        button.addEventListener("click", (event) => this.onQuickAdd(event, button));
      });

      this.hidden = false;
    }

    onQuickAdd(event, button) {
      event.preventDefault();
      const variantId = button.getAttribute("data-variant-id");
      if (!variantId || button.disabled) return;

      const originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = "…";

      const cartAddUrl = (window.routes && window.routes.cart_add_url) || "/cart/add.js";
      const cartUrl = (window.routes && window.routes.cart_url) || "/cart";

      fetch(cartAddUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      })
        .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
        .then(() => fetch(`${cartUrl}?section_id=cart-drawer`, { credentials: "same-origin" }))
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((html) => {
          const freshDrawer = new DOMParser().parseFromString(html, "text/html").getElementById("CartDrawer");
          const currentDrawer = document.getElementById("CartDrawer");
          if (freshDrawer && currentDrawer) {
            currentDrawer.innerHTML = freshDrawer.innerHTML;
          }
          const cartDrawerEl = document.querySelector("cart-drawer");
          if (cartDrawerEl && typeof cartDrawerEl.open === "function") {
            cartDrawerEl.open();
          }
        })
        .catch(() => {
          button.textContent = originalLabel;
          button.disabled = false;
          window.location.href = cartUrl;
        });
    }
  }

  customElements.define("cart-drawer-recommendations", CartDrawerRecommendations);

  window.CartDrawerRecommendations = {
    refresh() {
      const el = document.getElementById("CartDrawer-Recommendations");
      if (el && typeof el.refresh === "function") el.refresh();
    },
  };

  // Belt-and-suspenders trigger: this theme opens the cart drawer through
  // more than one code path (some of them bypass the <cart-drawer> element's
  // own open()/renderContents() methods entirely and toggle the "active"
  // class directly). Rather than depending on every one of those call sites
  // to know about recommendations, watch the drawer itself for the moment
  // it actually becomes visible and fetch then. This is what actually makes
  // the feature reliable regardless of which flow opened the drawer.
  function watchForDrawerOpen() {
    const drawer = document.querySelector("cart-drawer");
    if (!drawer) return;

    let wasActive = drawer.classList.contains("active");
    new MutationObserver(() => {
      const isActive = drawer.classList.contains("active");
      if (isActive && !wasActive) {
        window.CartDrawerRecommendations.refresh();
      }
      wasActive = isActive;
    }).observe(drawer, { attributes: true, attributeFilter: ["class"] });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", watchForDrawerOpen);
  } else {
    watchForDrawerOpen();
  }
})();
